import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { TrendingUpIcon, ClockIcon, TargetIcon } from 'lucide-react';
interface PredictionCardProps {
  homeTeam: string;
  awayTeam: string;
  homeTeamLogo: string;
  awayTeamLogo: string;
  competition: string;
  matchTime: string;
  odds: string;
  prediction: string;
  formRating: string;
  h2h: string;
  goalTrend: string;
  index: number;
}
export function PredictionCard({
  homeTeam,
  awayTeam,
  homeTeamLogo,
  awayTeamLogo,
  competition,
  matchTime,
  odds,
  prediction,
  formRating,
  h2h,
  goalTrend,
  index
}: PredictionCardProps) {
  const [isFlipped, setIsFlipped] = useState(false);
  return <motion.div initial={{
    opacity: 0,
    y: 30
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    duration: 0.5,
    delay: index * 0.1
  }} className="perspective-1000" data-id="element-157">
      <div className="relative h-[340px] md:h-[320px] cursor-pointer" onClick={() => setIsFlipped(!isFlipped)} data-id="element-158">
        {/* Front of Card */}
        <motion.div animate={{
        rotateY: isFlipped ? 180 : 0
      }} transition={{
        duration: 0.6
      }} className="absolute inset-0 backface-hidden" style={{
        backfaceVisibility: 'hidden'
      }} data-id="element-159">
          <div className="h-full bg-gradient-to-br from-dark-card to-dark-lighter rounded-2xl border border-neon-cyan/20 hover:border-neon-cyan/50 transition-all p-4 md:p-6 flex flex-col" data-id="element-160">
            {/* Competition & Time */}
            <div className="flex items-center justify-between mb-3 md:mb-4" data-id="element-161">
              <span className="text-xs font-semibold text-neon-cyan bg-neon-cyan/10 px-2.5 md:px-3 py-1 rounded-full" data-id="element-162">
                {competition}
              </span>
              <div className="flex items-center gap-1 text-xs text-gray-400" data-id="element-163">
                <ClockIcon className="w-3 h-3" data-id="element-164" />
                {matchTime}
              </div>
            </div>

            {/* Teams */}
            <div className="flex items-center justify-between mb-4 md:mb-6 flex-1" data-id="element-165">
              <div className="flex flex-col items-center gap-1.5 md:gap-2 flex-1 min-w-0" data-id="element-166">
                <div className="w-12 h-12 md:w-16 md:h-16 bg-white/10 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0" data-id="element-167">
                  <img src={homeTeamLogo} alt={homeTeam} className="w-9 h-9 md:w-12 md:h-12 object-contain" data-id="element-168" />
                </div>
                <span className="text-xs md:text-sm font-bold text-center line-clamp-2 px-1" data-id="element-169">
                  {homeTeam}
                </span>
              </div>

              <div className="px-2 md:px-4 flex-shrink-0" data-id="element-170">
                <div className="text-xl md:text-2xl font-black text-gray-600" data-id="element-171">
                  VS
                </div>
              </div>

              <div className="flex flex-col items-center gap-1.5 md:gap-2 flex-1 min-w-0" data-id="element-172">
                <div className="w-12 h-12 md:w-16 md:h-16 bg-white/10 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0" data-id="element-173">
                  <img src={awayTeamLogo} alt={awayTeam} className="w-9 h-9 md:w-12 md:h-12 object-contain" data-id="element-174" />
                </div>
                <span className="text-xs md:text-sm font-bold text-center line-clamp-2 px-1" data-id="element-175">
                  {awayTeam}
                </span>
              </div>
            </div>

            {/* Odds */}
            <div className="bg-neon-green/10 border border-neon-green/30 rounded-xl p-2.5 md:p-3 mb-2 md:mb-3" data-id="element-176">
              <div className="flex items-center justify-between" data-id="element-177">
                <span className="text-xs md:text-sm text-gray-400" data-id="element-178">Odds</span>
                <span className="text-xl md:text-2xl font-black text-neon-green" data-id="element-179">
                  {odds}
                </span>
              </div>
            </div>

            {/* Prediction */}
            <div className="bg-gradient-to-r from-neon-cyan/20 to-neon-purple/20 border border-neon-cyan/30 rounded-xl p-2.5 md:p-3" data-id="element-180">
              <div className="flex items-center justify-between gap-2" data-id="element-181">
                <span className="text-xs md:text-sm font-semibold text-gray-300" data-id="element-182">
                  Prediction:
                </span>
                <span className="text-sm md:text-base font-black text-white text-right" data-id="element-183">
                  {prediction}
                </span>
              </div>
            </div>

            {/* Tap to flip hint */}
            <div className="text-center mt-2 md:mt-3 text-xs text-gray-500" data-id="element-184">
              Tap for stats →
            </div>
          </div>
        </motion.div>

        {/* Back of Card */}
        <motion.div animate={{
        rotateY: isFlipped ? 0 : -180
      }} transition={{
        duration: 0.6
      }} className="absolute inset-0 backface-hidden" style={{
        backfaceVisibility: 'hidden',
        transform: 'rotateY(180deg)'
      }} data-id="element-185">
          <div className="h-full bg-gradient-to-br from-dark-lighter to-dark-card rounded-2xl border border-neon-purple/30 p-4 md:p-6 flex flex-col" data-id="element-186">
            <div className="flex items-center justify-between mb-4 md:mb-6" data-id="element-187">
              <h3 className="text-base md:text-lg font-black text-gradient-cyan" data-id="element-188">
                Match Stats
              </h3>
              <TargetIcon className="w-4 h-4 md:w-5 md:h-5 text-neon-purple" data-id="element-189" />
            </div>

            <div className="space-y-3 md:space-y-4 flex-1" data-id="element-190">
              <div className="bg-dark-card/50 rounded-xl p-3 md:p-4 border border-neon-green/20" data-id="element-191">
                <div className="flex items-center gap-2 mb-1.5 md:mb-2" data-id="element-192">
                  <div className="w-2 h-2 bg-neon-green rounded-full" data-id="element-193"></div>
                  <span className="text-xs md:text-sm font-semibold text-neon-green" data-id="element-194">
                    Form Rating
                  </span>
                </div>
                <p className="text-sm md:text-base text-white font-bold" data-id="element-195">
                  {formRating}
                </p>
              </div>

              <div className="bg-dark-card/50 rounded-xl p-3 md:p-4 border border-neon-cyan/20" data-id="element-196">
                <div className="flex items-center gap-2 mb-1.5 md:mb-2" data-id="element-197">
                  <div className="w-2 h-2 bg-neon-cyan rounded-full" data-id="element-198"></div>
                  <span className="text-xs md:text-sm font-semibold text-neon-cyan" data-id="element-199">
                    Head to Head
                  </span>
                </div>
                <p className="text-sm md:text-base text-white font-bold" data-id="element-200">
                  {h2h}
                </p>
              </div>

              <div className="bg-dark-card/50 rounded-xl p-3 md:p-4 border border-neon-purple/20" data-id="element-201">
                <div className="flex items-center gap-2 mb-1.5 md:mb-2" data-id="element-202">
                  <div className="w-2 h-2 bg-neon-purple rounded-full" data-id="element-203"></div>
                  <span className="text-xs md:text-sm font-semibold text-neon-purple" data-id="element-204">
                    Goal Trend
                  </span>
                </div>
                <p className="text-sm md:text-base text-white font-bold" data-id="element-205">
                  {goalTrend}
                </p>
              </div>
            </div>

            <div className="flex items-center justify-center gap-2 mt-3 md:mt-4 text-xs md:text-sm text-gray-400" data-id="element-206">
              <TrendingUpIcon className="w-4 h-4 text-neon-green" data-id="element-207" />
              <span className="font-semibold" data-id="element-208">Safe Pick</span>
            </div>

            {/* Tap to flip back hint */}
            <div className="text-center mt-2 md:mt-3 text-xs text-gray-500" data-id="element-209">
              ← Tap to return
            </div>
          </div>
        </motion.div>
      </div>
    </motion.div>;
}